/**
 * Cliente del API de comerciales (tabla propia `commercials` en el backend).
 *
 * Los comerciales dejaron de llenarse a mano: son una REFERENCIA a un usuario de
 * Identidad (`sub`). El nombre/foto/WhatsApp salen de su perfil SSO (snapshot que
 * el backend refresca solo). El alta se hace buscando al usuario en Identidad.
 *
 * La lectura pública (`GET /commercials`) sigue abierta; toda la GESTIÓN exige el
 * Bearer del admin del portal (guard nuevo — se cerró la deuda de auth).
 */
import { tokenStore } from '@/lib/auth/tokenStore'
import { getPortalApiBase } from '@/lib/env'

const ENDPOINT = `${getPortalApiBase()}/commercials`

/** Forma PÚBLICA que consume el FAB (modal "Contactar con un comercial"). */
export interface Commercial {
  id: string
  name: string
  photo: string
  whatsapp: string
  active: boolean
}

/** Forma de ADMIN: la referencia + el estado de frescura del snapshot. */
export interface AdminCommercial {
  id: string
  sub: string | null
  name: string
  photo: string
  /** WhatsApp derivado del teléfono de Identidad (lo que aplica sin override). */
  whatsapp: string
  /** WhatsApp alternativo fijado por el admin (manda sobre el de Identidad). */
  whatsappOverride: string | null
  /** WhatsApp efectivo (el que ve el público). */
  whatsappEffective: string
  active: boolean
  /** Última resolución del snapshot desde Identidad (ISO) o null si nunca. */
  syncedAt: string | null
  /** true si Identidad nunca pudo resolver esta fila (aviso para el admin). */
  unresolved: boolean
}

/** Candidato del buscador de Identidad para dar de alta un comercial. */
export interface IdentityCandidate {
  sub: string
  firstName: string
  lastName: string
  name: string
  email: string
  username: string
  photo: string
  phoneNumber: string
}

/** Alta: se referencia al usuario por `sub`. */
export interface CommercialCreateInput {
  sub: string
  active: boolean
  whatsappOverride?: string
}

/** Edición: el admin solo toca visibilidad y el WhatsApp alternativo. */
export interface CommercialUpdateInput {
  active?: boolean
  whatsappOverride?: string
}

export interface AdminCommercialsResult {
  items: AdminCommercial[]
  total: number
  page: number
  pageSize: number
}

/** Cabeceras con el Bearer del admin (mismo patrón que adminApi de pagos). */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

async function json<T>(res: Response, ctx: string): Promise<T> {
  if (!res.ok) throw new Error(`${ctx}: ${res.status}`)
  const text = await res.text()
  return (text.trim() ? JSON.parse(text) : null) as T
}

/** Listado público (solo activos). Sin auth. */
export async function fetchActiveCommercials(): Promise<Commercial[]> {
  const res = await fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
  return (await json<Commercial[] | null>(res, 'GET commercials')) ?? []
}

/** Listado de administración: paginado + búsqueda. Requiere admin. */
export async function fetchAdminCommercials(query: {
  page?: number
  pageSize?: number
  q?: string
}): Promise<AdminCommercialsResult> {
  const qs = new URLSearchParams()
  if (query.page) qs.set('page', String(query.page))
  if (query.pageSize) qs.set('pageSize', String(query.pageSize))
  if (query.q) qs.set('q', query.q)
  const res = await fetch(`${ENDPOINT}/admin?${qs}`, { headers: authHeaders() })
  const data = await json<AdminCommercialsResult | null>(res, 'GET commercials/admin')
  return data ?? { items: [], total: 0, page: 1, pageSize: query.pageSize ?? 10 }
}

/** Busca usuarios de Identidad (por email/username) para el alta. Requiere admin. */
export async function searchIdentityUsers(q: string): Promise<IdentityCandidate[]> {
  const term = q.trim()
  if (term.length < 2) return []
  const res = await fetch(
    `${ENDPOINT}/identity-search?q=${encodeURIComponent(term)}`,
    { headers: authHeaders() },
  )
  const data = await json<{ items: IdentityCandidate[] } | null>(
    res,
    'GET commercials/identity-search',
  )
  return data?.items ?? []
}

export async function createCommercial(
  input: CommercialCreateInput,
): Promise<AdminCommercial> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  })
  return json<AdminCommercial>(res, 'POST commercials')
}

export async function updateCommercial(
  id: string,
  input: CommercialUpdateInput,
): Promise<AdminCommercial> {
  const res = await fetch(`${ENDPOINT}/${id}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  })
  return json<AdminCommercial>(res, 'PUT commercials/:id')
}

/** Re-resuelve el snapshot de un comercial desde Identidad. Requiere admin. */
export async function refreshCommercial(id: string): Promise<AdminCommercial> {
  const res = await fetch(`${ENDPOINT}/${id}/refresh`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return json<AdminCommercial>(res, 'POST commercials/:id/refresh')
}

export async function deleteCommercial(id: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`DELETE commercials/:id: ${res.status}`)
}
